import { validateBoardDef, type BoardDef } from '@archon3d/core';

/** Boards drawn in the editor, kept in this browser as a JSON list of `BoardDef`s. */
export const BOARDS_KEY = 'archon3d.boards';

export interface BoardStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function browserStorage(): BoardStorage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** The saved boards that still validate; a broken entry is skipped rather than breaking the list. */
export function loadSavedBoards(storage: BoardStorage | null = browserStorage()): BoardDef[] {
  let raw: unknown;
  try {
    const text = storage?.getItem(BOARDS_KEY);
    raw = text ? JSON.parse(text) : [];
  } catch {
    return [];
  }
  if (!Array.isArray(raw)) return [];
  const out: BoardDef[] = [];
  const seen = new Set<string>();
  for (const entry of raw) {
    try {
      const def = validateBoardDef(entry);
      if (!seen.has(def.id)) {
        seen.add(def.id);
        out.push(def);
      }
    } catch {
      // Left over from an older format or edited by hand: not worth losing the others over.
    }
  }
  return out;
}

export function storeSavedBoards(
  boards: readonly BoardDef[],
  storage: BoardStorage | null = browserStorage(),
): void {
  try {
    storage?.setItem(BOARDS_KEY, JSON.stringify(boards));
  } catch {
    // Private mode or a full store: the boards last for this visit.
  }
}

/** Replaces the board with the same id, or appends. */
export function upsertBoard(boards: readonly BoardDef[], def: BoardDef): BoardDef[] {
  const index = boards.findIndex((b) => b.id === def.id);
  if (index < 0) return [...boards, def];
  return boards.map((b, i) => (i === index ? def : b));
}

export function removeBoard(boards: readonly BoardDef[], id: string): BoardDef[] {
  return boards.filter((b) => b.id !== id);
}

/** A board from an exported file (or any JSON), validated; throws with a readable message. */
export function parseBoardJson(text: string): BoardDef {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    throw new Error('The file is not valid JSON.');
  }
  return validateBoardDef(raw);
}

export function boardToJson(def: BoardDef): string {
  return JSON.stringify(def, null, 2);
}
